import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { LIBRARY_CATEGORIES, LIBRARY_RESOURCES } from '../data/library';
import { CONCERN_MODULE_MAP } from '../data/modules';
import { MODULE_LIBRARY_MAP } from '../data/library';
import { listLibrary } from '../lib/api';
import MediaEmbed from '../components/MediaEmbed';
import ImageLightbox from '../components/ImageLightbox';
import { resolveLibraryMedia } from '../lib/mediaEmbed';
import { Search, Play, BookOpen, ImageIcon, X, ArrowRight, MessageCircle, Calendar, Sparkles, Maximize2 } from 'lucide-react';

const STORAGE_KEY = 'app_selfcheck';

const TYPE_META = {
  video: { Icon: Play, labelZh: '视频', labelEn: 'Video', cls: 'bg-terracotta-light text-terracotta' },
  image_guide: { Icon: ImageIcon, labelZh: '图文', labelEn: 'Image Guide', cls: 'bg-sage-light text-sage' },
  guide: { Icon: BookOpen, labelZh: '指南', labelEn: 'Guide', cls: 'bg-subtle text-ink-secondary' },
};

// Card thumbnail — uses resolved media (thumbnail_url → YouTube thumb → first
// image → legacy media_url) and falls back to a gradient + icon when nothing
// usable is available. Backwards-compatible with the original schema.
function ResourceThumb({ resource }) {
  const meta = TYPE_META[resource.type];
  const Icon = meta.Icon;
  const { thumbnailUrl } = resolveLibraryMedia(resource);

  const gradient =
    resource.type === 'video'
      ? 'from-terracotta-light via-terracotta-light/60 to-paper'
      : resource.type === 'image_guide'
      ? 'from-sage-light via-sage-light/60 to-paper'
      : 'from-subtle via-subtle/60 to-paper';

  if (thumbnailUrl) {
    return (
      <div className="relative h-36 rounded-2xl overflow-hidden bg-subtle">
        <img
          src={thumbnailUrl}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          data-testid="resource-thumb-img"
        />
        {resource.type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-paper/85 backdrop-blur flex items-center justify-center shadow-md">
              <Play size={18} className="text-terracotta ml-0.5" />
            </div>
          </div>
        )}
        {resource.type === 'video' && resource.duration && (
          <span className="absolute bottom-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-ink/70 text-white">
            {resource.duration}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`relative h-36 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center overflow-hidden`}>
      <div className="w-14 h-14 rounded-full bg-paper/70 backdrop-blur flex items-center justify-center shadow-sm">
        <Icon size={22} className="text-ink-secondary" />
      </div>
      {resource.type === 'video' && resource.duration && (
        <span className="absolute bottom-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-ink/70 text-white">
          {resource.duration}
        </span>
      )}
    </div>
  );
}

export default function Library() {
  const { lang, t } = useLanguage();
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [selected, setSelected] = useState(null);
  const [selfCheckData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [resources, setResources] = useState(LIBRARY_RESOURCES);

  // Fetch live resources from API (admin-editable media URLs); fall back to static seed on error.
  useEffect(() => {
    let cancelled = false;
    listLibrary()
      .then((items) => {
        if (cancelled) return;
        if (Array.isArray(items) && items.length > 0) {
          // Merge: prefer API data but keep static content_zh/content_en if API row lacks them.
          const staticById = Object.fromEntries(LIBRARY_RESOURCES.map((r) => [r.id, r]));
          const merged = items.map((r) => {
            const s = staticById[r.id] || {};
            return {
              ...s,
              ...r,
              content_zh: (r.content_zh && r.content_zh.length) ? r.content_zh : (s.content_zh || []),
              content_en: (r.content_en && r.content_en.length) ? r.content_en : (s.content_en || []),
            };
          });
          setResources(merged);
        }
      })
      .catch(() => { /* keep static fallback */ });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return resources.filter((r) => {
      if (activeCat !== 'all' && r.category !== activeCat) return false;
      if (q) {
        const text = (lang === 'zh' ? r.title_zh + r.desc_zh : r.title_en + r.desc_en).toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [search, activeCat, lang, resources]);

  // Recommended for you based on self-check
  const recommendedIds = useMemo(() => {
    if (!selfCheckData?.main_concern) return [];
    const moduleId = selfCheckData.module_id || CONCERN_MODULE_MAP[selfCheckData.main_concern];
    return MODULE_LIBRARY_MAP[moduleId] || [];
  }, [selfCheckData]);

  const recommended = recommendedIds
    .map((id) => resources.find((r) => r.id === id))
    .filter(Boolean);

  return (
    <div className="container-app py-8 sm:py-12 animate-fade-in-up" data-testid="library-page">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta">
          {lang === 'zh' ? '资料库' : 'Library'}
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink mt-2 mb-3">
          {lang === 'zh' ? '哺乳资料库' : 'Breastfeeding Knowledge Library'}
        </h1>
        <p className="text-ink-secondary leading-relaxed max-w-2xl">
          {lang === 'zh'
            ? '这里整理了妈妈最常遇到的哺乳问题和实用教学，你可以根据标题选择观看图片、视频或图文步骤。'
            : 'Browse practical breastfeeding guides, images, videos, and step-by-step resources based on common concerns mothers experience.'}
        </p>
      </div>

      {/* Recommended for you */}
      {recommended.length > 0 && (
        <section className="mb-10" data-testid="recommended-section">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-terracotta" />
            <h2 className="font-heading text-lg font-semibold text-ink">
              {lang === 'zh' ? '为你推荐' : 'Recommended for you'}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommended.map((r) => (
              <ResourceCard key={`rec-${r.id}`} resource={r} onOpen={() => setSelected(r)} lang={lang} />
            ))}
          </div>
        </section>
      )}

      {/* Search */}
      <div className="relative mb-6">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-tertiary" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={lang === 'zh' ? '搜索资源标题、关键字...' : 'Search title or keyword...'}
          className="input-base pl-12"
          data-testid="library-search"
        />
      </div>

      {/* Category filters */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-thin" data-testid="library-categories">
        <button
          onClick={() => setActiveCat('all')}
          className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            activeCat === 'all' ? 'bg-terracotta text-white' : 'bg-paper border border-border text-ink-secondary hover:border-terracotta'
          }`}
          data-testid="cat-all"
        >
          {lang === 'zh' ? '全部' : 'All'}
        </button>
        {LIBRARY_CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveCat(c.id)}
            data-testid={`cat-${c.id}`}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeCat === c.id
                ? 'bg-terracotta text-white'
                : 'bg-paper border border-border text-ink-secondary hover:border-terracotta'
            }`}
          >
            {lang === 'zh' ? c.zh : c.en}
          </button>
        ))}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="card-base p-12 text-center text-ink-tertiary" data-testid="library-empty">
          {lang === 'zh' ? '没有找到匹配的资源' : 'No resources match your search'}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((r) => (
            <ResourceCard key={r.id} resource={r} onOpen={() => setSelected(r)} lang={lang} />
          ))}
        </div>
      )}

      {/* CTA */}
      <section className="mt-16 card-base p-6 sm:p-10 bg-gradient-to-br from-terracotta-light/40 to-sage-light/30">
        <h3 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">
          {lang === 'zh' ? '看完资料还是不确定怎么处理？' : 'Still unsure after browsing?'}
        </h3>
        <p className="text-ink-secondary mb-6 max-w-xl">
          {lang === 'zh'
            ? '完成 Self-check，让系统帮你判断；或直接预约 Soo 的咨询。'
            : 'Complete the self-check for guided advice — or book a consultation with Soo directly.'}
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/self-check" className="btn-primary" data-testid="lib-cta-selfcheck">
            {t('landing_btn_start')} <ArrowRight size={16} className="ml-2" />
          </Link>
          <Link to="/booking" className="btn-secondary" data-testid="lib-cta-book">
            <Calendar size={16} className="mr-2" /> {t('landing_btn_book')}
          </Link>
        </div>
      </section>

      {/* Detail Modal */}
      {selected && (
        <ResourceDetail
          resource={selected}
          resources={resources}
          onClose={() => setSelected(null)}
          onOpen={(r) => setSelected(r)}
          lang={lang}
          t={t}
        />
      )}
    </div>
  );
}

function ResourceCard({ resource, onOpen, lang }) {
  const meta = TYPE_META[resource.type];
  const Icon = meta.Icon;
  return (
    <button
      onClick={onOpen}
      data-testid={`resource-${resource.id}`}
      className="card-interactive text-left overflow-hidden group"
    >
      <div className="p-3 pb-0">
        <ResourceThumb resource={resource} />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.cls}`}>
            <Icon size={10} /> {lang === 'zh' ? meta.labelZh : meta.labelEn}
          </span>
          {resource.duration && (
            <span className="text-[10px] text-ink-tertiary">{resource.duration}</span>
          )}
        </div>
        <h3 className="font-heading font-semibold text-ink mb-1 group-hover:text-terracotta transition-colors leading-snug">
          {lang === 'zh' ? resource.title_zh : resource.title_en}
        </h3>
        <p className="text-xs text-ink-secondary leading-relaxed line-clamp-2">
          {lang === 'zh' ? resource.desc_zh : resource.desc_en}
        </p>
      </div>
    </button>
  );
}

// Responsive image gallery for 1–3 supporting images.
// • 1 image → full-width row
// • 2 images → 2 equal columns
// • 3 images → 3 columns on ≥sm, stacked on mobile
function ImageGallery({ images, alt, onOpen }) {
  if (!images || images.length === 0) return null;
  const cols = images.length === 1 ? 'grid-cols-1' : images.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3';
  return (
    <div className={`grid ${cols} gap-2`} data-testid="library-image-gallery">
      {images.map((src, i) => (
        <button
          type="button"
          key={`${src}-${i}`}
          onClick={() => onOpen(i)}
          data-testid={`gallery-image-${i}`}
          className="relative group overflow-hidden rounded-2xl bg-subtle aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-terracotta"
          aria-label={`Open image ${i + 1}`}
        >
          <img
            src={src}
            alt={`${alt} ${i + 1}`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            onError={(e) => { e.currentTarget.style.opacity = '0.25'; }}
          />
          <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/15 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="w-9 h-9 rounded-full bg-paper/90 flex items-center justify-center shadow">
              <Maximize2 size={14} className="text-ink" />
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ResourceDetail({ resource, resources, onClose, onOpen, lang, t }) {
  const meta = TYPE_META[resource.type];
  const Icon = meta.Icon;
  const category = LIBRARY_CATEGORIES.find((c) => c.id === resource.category);
  const content = lang === 'zh' ? resource.content_zh : resource.content_en;
  const title = lang === 'zh' ? resource.title_zh : resource.title_en;

  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // Resolve the three normalised buckets out of the resource (handles both
  // new fields and the legacy media_url).
  const { videoUrl, imageUrls } = useMemo(() => resolveLibraryMedia(resource), [resource]);
  const hasVideo = !!videoUrl;
  const hasImages = imageUrls.length > 0;
  const hasAnyMedia = hasVideo || hasImages;

  // Related: same category, exclude self
  const pool = Array.isArray(resources) && resources.length ? resources : LIBRARY_RESOURCES;
  const related = pool.filter((r) => r.category === resource.category && r.id !== resource.id).slice(0, 3);

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="resource-detail-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-paper rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[92vh] sm:max-h-[88vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-paper border-b border-border/40 px-5 sm:px-8 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.cls}`}>
              <Icon size={10} /> {lang === 'zh' ? meta.labelZh : meta.labelEn}
            </span>
            {category && (
              <span className="text-xs text-ink-tertiary truncate">
                · {lang === 'zh' ? category.zh : category.en}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-subtle flex-shrink-0" data-testid="resource-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 sm:px-8 pb-8">
          {/* Media block: video first, then image gallery. Falls back to the
              gradient placeholder when nothing is available. */}
          <div className="my-5 space-y-3" data-testid="library-media-block">
            {hasVideo && (
              <div data-testid="library-video-block">
                <MediaEmbed url={videoUrl} title={title} />
              </div>
            )}
            {hasImages && (
              <ImageGallery
                images={imageUrls}
                alt={title}
                onOpen={(idx) => setLightboxIndex(idx)}
              />
            )}
            {!hasAnyMedia && <ResourceThumb resource={resource} />}
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-3 leading-snug">
            {title}
          </h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            {lang === 'zh' ? resource.desc_zh : resource.desc_en}
          </p>

          {Array.isArray(content) && content.length > 0 && (
            <div className="card-base p-5 sm:p-6 mb-6 bg-subtle/40">
              <h3 className="font-heading text-sm font-semibold text-ink-secondary uppercase tracking-wider mb-3">
                {lang === 'zh' ? '内容要点' : 'Key Points'}
              </h3>
              <ol className="space-y-3">
                {content.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="w-7 h-7 rounded-full bg-terracotta text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {i + 1}
                    </span>
                    <p className="text-ink-secondary leading-relaxed pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Related */}
          {related.length > 0 && (
            <div className="mb-6">
              <h3 className="font-heading text-sm font-semibold text-ink-secondary uppercase tracking-wider mb-3">
                {lang === 'zh' ? '相关资源' : 'Related Resources'}
              </h3>
              <div className="grid gap-3">
                {related.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => onOpen(r)}
                    data-testid={`related-${r.id}`}
                    className="card-interactive flex items-center gap-3 p-3 text-left"
                  >
                    <div className="w-12 h-12 rounded-xl bg-subtle flex items-center justify-center flex-shrink-0">
                      <Icon size={16} className="text-ink-tertiary" />
                    </div>
                    <span className="text-sm font-medium text-ink line-clamp-2">
                      {lang === 'zh' ? r.title_zh : r.title_en}
                    </span>
                    <ArrowRight size={14} className="ml-auto text-ink-tertiary flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CTAs */}
          <div className="grid sm:grid-cols-3 gap-2">
            <Link to="/self-check" onClick={onClose} className="btn-primary text-sm" data-testid="detail-cta-selfcheck">
              {t('landing_btn_start')}
            </Link>
            <Link to="/whatsapp-summary" onClick={onClose} className="btn-secondary text-sm" data-testid="detail-cta-whatsapp">
              <MessageCircle size={14} className="mr-1" /> WhatsApp
            </Link>
            <Link to="/booking" onClick={onClose} className="btn-outline text-sm" data-testid="detail-cta-book">
              {t('landing_btn_book')}
            </Link>
          </div>
        </div>
      </div>

      {/* Lightbox lives at the top of the modal stack */}
      {lightboxIndex >= 0 && (
        <ImageLightbox
          images={imageUrls}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(-1)}
          onIndex={setLightboxIndex}
          alt={title}
        />
      )}
    </div>
  );
}
